'use client';

import { useWriteTransition } from '@/lib/refresh-gate';
import { toggleViewStar } from '@/app/actions';
import { IconButton } from './ui';
import { PushPin } from './icons';
import { reach } from '@/lib/reach';
import { useT } from '@/lib/locale-context';

/**
 * The "pin a view" toggle on a nav Views row. A filled pushpin when pinned, an
 * outline otherwise; clicking flips it through the off-log `toggleViewStar` action
 * and refreshes so the nav regroups (pinned views float into the Favorites group).
 * Styling (reveal-on-hover, the pinned fill) is passed in via `className` from the
 * row's own CSS module, so the hover selector and the `.viewRow` live together.
 *
 * NOTE: user-visible wording is "pin"/"unpin" and the glyph is a pushpin, but the
 * INTERNAL naming stays `star` on purpose (component `ViewStar`, prop `starred`,
 * `data-starred`, the `view_stars` table/lib): "pin" already names a different
 * feature in the code (`view_pins` = items pinned to a view), so we avoid the
 * collision by keeping `star` everywhere below the surface.
 */
export function ViewStar({
  viewId,
  starred,
  className,
}: {
  viewId: string;
  starred: boolean;
  className?: string;
}) {
  const t = useT();
  const [pending, start] = useWriteTransition();

  function toggle() {
    start(async () => {
      // No seat for a message on the star: a network failure changes nothing.
      await reach(() => toggleViewStar({ viewId, starred: !starred }));
    });
  }

  return (
    <IconButton
      label={starred ? t('viewControls.star.unpin') : t('viewControls.star.pin')}
      onClick={toggle}
      pending={pending}
      className={className}
      data-starred={starred || undefined}
    >
      <PushPin weight={starred ? 'fill' : 'regular'} aria-hidden />
    </IconButton>
  );
}
